import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useId, useRef } from 'react';
import { MODAL_PART_CLASSES, modalBackdropClasses, modalDialogClasses } from './styles.js';
import { useUiV2 } from './v2.js';
// Tab 移動の対象になりうる要素。focus trap でこの集合を dialog 内に閉じ込める。
const FOCUSABLE_SELECTOR = [
    'a[href]',
    'area[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
].join(',');
/**
 * dialog 内の focusable 要素を DOM 順で返す。
 *
 * 可視判定は `hidden` 属性 / `aria-hidden` のみで行う。`offsetParent` /
 * `getClientRects()` によるレイアウトベースの可視判定は happy-dom（テスト環境）が
 * 実レイアウトを持たず信頼できないため使わない。modal 内に `display:none` の
 * focusable を置くケースは稀で、実害は小さい（trap 対象に含まれても視覚的 no-op）。
 */
function getFocusableElements(container) {
    return Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR)).filter((el) => !el.hasAttribute('hidden') && el.getAttribute('aria-hidden') !== 'true');
}
// body スクロールロックの参照カウント（モジュール共有）。
// modal は単一インスタンスでも複数同時 open（一覧の上に確認ダイアログ等）でも使われうる。
// 各 effect が独立に save/restore すると、後発 modal が「既に hidden」を original として掴み
// 永久ロックになる。0→1 の遷移でだけ original を退避して 'hidden'、1→0 でだけ復帰することで
// 多重 open / 非 LIFO な close 順でも最後の 1 つが閉じた時に正しく戻す。
let scrollLockCount = 0;
let scrollLockOriginalOverflow = '';
/**
 * モーダルダイアログ primitive。各 app に inline 重複していた
 * backdrop（fixed inset-0 bg-black/40）+ ダイアログ面 + ヘッダ/フッタを集約する。
 *
 * a11y（S-69）:
 * - Escape（closeOnEscape）/ backdrop クリック（closeOnBackdrop）で `onClose`
 * - `role="dialog"` / `aria-modal="true"` / title 有り時 `aria-labelledby`
 * - 初期 focus を dialog 内（最初の focusable、無ければ dialog 自身）へ移す
 * - close / unmount 時に開く前の要素へ focus を復帰
 * - Tab / Shift+Tab を dialog 内の focusable に閉じ込める（focus trap）。trap は
 *   **document レベル（capture）**で購読する。React の onKeyDown はルートコンテナへ
 *   委譲されるため、focus が何らかの理由で dialog 部分木の外（body 等）へ落ちると
 *   発火せず trap が破れる。document リスナなら外側 focus からの Tab も回収できる。
 * - open 中は body スクロールをロック（参照カウントで多重 open に対応）
 *
 * 既知の制約:
 * - **背景の読み上げは抑止していない**。`aria-modal="true"` は支援技術へのヒントに
 *   過ぎず、スクリーンリーダーの仮想カーソル（VoiceOver VO+矢印 / NVDA ブラウズモード）
 *   は背景コンテンツへ到達できる。完全に隠すには背景要素への `inert` 付与が要るが、
 *   non-portal（後述）では dialog の兄弟へ inert を撒く実装が煩雑で、portal 化と併せた
 *   別対応とする（S-69 で判断 / 受け入れ条件外）。
 * - portal は採用しない（S-69）。現行 call-site のレイアウト祖先に `transform`/`filter`
 *   等の包含ブロック生成要因が無く `position: fixed` が崩れないこと、design-sync の
 *   プレビューが非 portal 挙動を前提にしていることが理由。将来 transform 祖先が現れた
 *   場合は createPortal 化（+ 背景 inert + design-sync 再 sync）を別途検討する。
 */
export function Modal({ open, onClose, title, size, v2, footer, closeOnBackdrop = true, closeOnEscape = true, children, }) {
    const isV2 = useUiV2(v2);
    const parts = MODAL_PART_CLASSES[isV2 ? 'v2' : 'v1'];
    const titleId = useId();
    const dialogRef = useRef(null);
    // open 直前に focus していた要素（close 時にここへ戻す）。
    const previouslyFocusedRef = useRef(null);
    // onClose / closeOnEscape を ref に退避し、キーボードリスナを毎 render
    // 再購読しない（deps は open のみ）。
    const onCloseRef = useRef(onClose);
    const closeOnEscapeRef = useRef(closeOnEscape);
    useEffect(() => {
        onCloseRef.current = onClose;
        closeOnEscapeRef.current = closeOnEscape;
    }, [onClose, closeOnEscape]);
    // キーボード制御（open の間 / document capture）:
    //   - Escape: closeOnEscape なら onClose
    //   - Tab / Shift+Tab: dialog 内の focusable に閉じ込める（focus trap）
    useEffect(() => {
        if (!open)
            return;
        function onKeyDown(e) {
            if (e.key === 'Escape') {
                if (closeOnEscapeRef.current)
                    onCloseRef.current();
                return;
            }
            if (e.key !== 'Tab')
                return;
            const dialog = dialogRef.current;
            if (!dialog)
                return;
            const focusables = getFocusableElements(dialog);
            const first = focusables[0];
            const last = focusables[focusables.length - 1];
            const active = document.activeElement;
            // focusable が無ければ dialog 自身に留め、外へ出さない。
            if (!first || !last) {
                e.preventDefault();
                dialog.focus();
                return;
            }
            if (e.shiftKey) {
                if (active === first || !dialog.contains(active)) {
                    e.preventDefault();
                    last.focus();
                }
            }
            else if (active === last || !dialog.contains(active)) {
                e.preventDefault();
                first.focus();
            }
        }
        document.addEventListener('keydown', onKeyDown, true);
        return () => document.removeEventListener('keydown', onKeyDown, true);
    }, [open]);
    // body スクロールロック（open の間 / 参照カウントで多重 open に対応）。
    useEffect(() => {
        if (!open)
            return;
        if (scrollLockCount === 0) {
            scrollLockOriginalOverflow = document.body.style.overflow;
            document.body.style.overflow = 'hidden';
        }
        scrollLockCount += 1;
        return () => {
            scrollLockCount -= 1;
            if (scrollLockCount === 0) {
                document.body.style.overflow = scrollLockOriginalOverflow;
            }
        };
    }, [open]);
    // 初期 focus（open 時）+ focus 復帰（close / unmount 時）。
    useEffect(() => {
        if (!open)
            return;
        previouslyFocusedRef.current =
            document.activeElement instanceof HTMLElement
                ? document.activeElement
                : null;
        const dialog = dialogRef.current;
        if (dialog) {
            const focusables = getFocusableElements(dialog);
            (focusables[0] ?? dialog).focus();
        }
        return () => {
            const prev = previouslyFocusedRef.current;
            if (prev && prev.isConnected)
                prev.focus();
        };
    }, [open]);
    if (!open)
        return null;
    return (_jsx("div", { className: modalBackdropClasses(), onClick: closeOnBackdrop ? onClose : undefined, children: _jsxs("div", { ref: dialogRef, role: "dialog", "aria-modal": "true", "aria-labelledby": title != null ? titleId : undefined, tabIndex: -1, className: modalDialogClasses({ size, v2: isV2 }), onClick: (e) => e.stopPropagation(), children: [title != null && (_jsxs("div", { className: parts.header, children: [_jsx("h2", { id: titleId, className: parts.title, children: title }), _jsx("button", { type: "button", onClick: onClose, "aria-label": "\u9589\u3058\u308B", className: parts.close, children: _jsx("span", { "aria-hidden": "true", children: "\u00D7" }) })] })), _jsx("div", { className: parts.body, children: children }), footer != null && (_jsx("div", { className: parts.footer, children: footer }))] }) }));
}
//# sourceMappingURL=Modal.js.map